import { useEffect, useState } from 'react';
import { RightOutlined } from '@ant-design/icons';
import { motion, useReducedMotion } from 'motion/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { type SupportedLanguage } from '@/locales';
import whiteLogo from '@/assets/imgs/home/contact/bilionpass-logo.png';
import airportShopping from '@/assets/imgs/home/services/airport-shopping.png';
import b2bBubble from '@/assets/imgs/home/services/b2b-bubble.svg';
import coupon from '@/assets/imgs/home/services/coupon.png';
import laptop from '@/assets/imgs/home/services/laptop.png';
import luggage from '@/assets/imgs/home/services/luggage.png';
import phoneLeft from '@/assets/imgs/home/services/phone-left.png';
import phoneRight from '@/assets/imgs/home/services/phone-right.png';
import vip from '@/assets/imgs/home/services/vip.png';
import taxRefund from '@/assets/imgs/home/services/tax-refund.png';
import transport from '@/assets/imgs/home/services/transport.png';
import tickets from '@/assets/imgs/home/services/tickets.png';
import hotel from '@/assets/imgs/home/services/hotel.png';
import serviceQuote from '@/assets/imgs/home/services/quote.svg';
import decorAirport from '@/assets/imgs/home/services/decor-airport.svg';
import decorLuggage from '@/assets/imgs/home/services/decor-luggage.svg';
import decorCoupon from '@/assets/imgs/home/services/decor-coupon.svg';
import decorVip from '@/assets/imgs/home/services/decor-vip.svg';
import decorTax from '@/assets/imgs/home/services/decor-tax.svg';
import pageArrowRight from '@/assets/imgs/home/services/page-arrow-right.svg';
import shopDevices from '@/assets/imgs/home/shops/devices.png';
import shopDecorLeft from '@/assets/imgs/home/shops/decor-left.png';
import shopDecorRight from '@/assets/imgs/home/shops/decor-right.png';
import featureCoupon from '@/assets/imgs/home/shops/feature-coupon.svg';
import featureLogistics from '@/assets/imgs/home/shops/feature-logistics.svg';
import featurePayment from '@/assets/imgs/home/shops/feature-payment.svg';
import featureTax from '@/assets/imgs/home/shops/feature-tax.svg';
import toShopsBubble from '@/assets/imgs/home/shops/to-shops-bubble.svg';
import additionalGlow from '@/assets/imgs/home/additional/glow.svg';
import hrImage from '@/assets/imgs/home/additional/hr.png';
import marketingImage from '@/assets/imgs/home/additional/marketing.png';
import marketingArrow from '@/assets/imgs/home/additional/arrow-marketing.svg';
import hrArrow from '@/assets/imgs/home/additional/arrow-hr.svg';
import newsImage1 from '@/assets/imgs/home/news/news-1.png';
import newsImage2 from '@/assets/imgs/home/news/news-2.png';
import newsImage3 from '@/assets/imgs/home/news/news-3.png';
import { newsPage, type NewsRecord } from '@/service/api/news';
import contactMask from '@/assets/imgs/home/contact/mask.png';
import messageIcon from '@/assets/imgs/home/contact/message.svg';
import JourneyPlane from './JourneyPlane';
import { useDesignScale } from '@/hooks/useDesignScale';

const serviceCards = [
  { image: airportShopping, decoration: decorAirport, key: 'airportShopping' },
  { image: luggage, decoration: decorLuggage, key: 'luggage' },
  { image: coupon, decoration: decorCoupon, key: 'coupon' },
  { image: vip, decoration: decorVip, key: 'vip' },
  { image: taxRefund, decoration: decorTax, key: 'taxRefund' },
  { image: transport, decoration: decorLuggage, key: 'transport' },
  { image: tickets, decoration: decorCoupon, key: 'tickets' },
  { image: hotel, decoration: decorVip, key: 'hotel' },
];

const servicePages = [serviceCards.slice(0, 4), serviceCards.slice(4, 8)];
const servicePaths: Record<(typeof serviceCards)[number]['key'], string> = {
  airportShopping: '/duty-free-pre-order',
  luggage: '/airport-luggage-delivery',
  coupon: '/coupon',
  vip: '/vip',
  taxRefund: '/',
  transport: '/',
  tickets: '/',
  hotel: '/',
};
const bilionpassServiceUrl = (key: (typeof serviceCards)[number]['key'], language: SupportedLanguage) =>
  `https://www.bilionpass.com${servicePaths[key]}?lang=${encodeURIComponent(language)}`;

const shopFeatures = [
  { icon: featureCoupon, key: 'coupon' },
  { icon: featureLogistics, key: 'logistics' },
  { icon: featureTax, key: 'tax' },
  { icon: featurePayment, key: 'payment' },
];

type AdditionalCard = {
  image: string;
  arrow: string;
  key: 'marketing' | 'hr';
  color: string;
  href?: string;
};

const additionalCards: AdditionalCard[] = [
  { image: marketingImage, arrow: marketingArrow, key: 'marketing', color: '#00a0e9' },
  {
    image: hrImage,
    arrow: hrArrow,
    key: 'hr',
    color: '#ff5d21',
    href: 'https://www.vistapath.co.jp',
  },
];

const fallbackNewsImages = [newsImage1, newsImage2, newsImage3];
const NEWS_PAGE_SIZE = 3;
const NEWS_MAX_PAGES = 3;

const Canvas = ({ children }: { children: React.ReactNode }) => (
  <div className="relative left-1/2 h-full w-[1920px] -translate-x-1/2">{children}</div>
);

const ViewMoreButton = ({ inverse = false, to }: { inverse?: boolean; to: string }) => {
  return (
    <Link
      to={to}
      className={`group inline-flex h-[60px] w-[240px] items-center justify-center overflow-hidden rounded-[85px] border-2 border-[#00a0e9] text-[28px] font-medium transition-[background-color,color,border-color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:duration-0 ${
        inverse
          ? 'bg-white text-[#00a0e9] hover:border-white hover:bg-[#00a0e9] hover:text-white'
          : 'bg-[#00a0e9] text-white hover:bg-white hover:text-[#00a0e9]'
      }`}
    >
      <span className="flex items-center gap-[10px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-[16px] motion-reduce:duration-0">
        View More
        <RightOutlined
          aria-hidden="true"
          className="text-[22px] opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
        />
      </span>
    </Link>
  );
};

const TravelsSection = () => {
  const { t } = useTranslation();
  const language = (i18n.resolvedLanguage ?? i18n.language) as SupportedLanguage;
  const reduceMotion = useReducedMotion();
  const designScale = useDesignScale();
  const [activeServicePage, setActiveServicePage] = useState(0);
  const [servicePageTransition, setServicePageTransition] = useState(0);
  const [servicePageDirection, setServicePageDirection] = useState<-1 | 1>(1);

  const changeServicePage = (nextPage: number) => {
    if (nextPage === activeServicePage) return;

    setServicePageDirection(nextPage > activeServicePage ? 1 : -1);
    setActiveServicePage(nextPage);
    setServicePageTransition((transition) => transition + 1);
  };

  const servicePageDistance = 1432 * designScale;
  const servicePageTarget = activeServicePage * -servicePageDistance;
  const servicePageStart = -(activeServicePage - servicePageDirection) * servicePageDistance;
  const servicePageAnticipation = servicePageStart + servicePageDirection * 64 * designScale;

  return (
    <section data-journey-stop="travels" className="relative h-[1686px] overflow-hidden rounded-t-[80px] bg-gradient-to-r from-[#26A8FF] to-[#028AE5] text-white z-[3]">
      <Canvas>
        <div
          className="absolute left-[1252px] top-[56px] z-30 h-[48px] w-[164px]"
          aria-label="B To B To C"
        >
          <img
            src={b2bBubble}
            alt=""
            className="absolute left-[2px] top-[2px] h-[45px] w-[160px]"
          />
          <span className="absolute inset-x-0 top-[3px] text-center text-[24px] font-medium leading-[1.5] text-white">
            B To B To C
          </span>
        </div>

        <div className="relative z-20 flex flex-col items-center pt-[78px] text-center">
          <div className="flex items-center gap-[14px]">
            <img src={whiteLogo} alt="BilionPass" className="h-[80px] w-[355px]" />
            <h2 className="font-expose text-[80px] font-bold leading-none translate-y-[10px]">Travels</h2>
          </div>
          <p className="mt-[22px] w-[1450px] whitespace-pre-line text-[32px] leading-[1.55]">
            {t('home.travels.description')}
          </p>
          <div className="mt-[28px]"><ViewMoreButton inverse to="/travel" /></div>
        </div>

        <div className="absolute left-[260px] top-[410px] z-20 h-[480px] w-[1400px]">
          <motion.img
            src={phoneLeft}
            alt=""
            className="absolute left-0 top-[25px] h-[465px] w-[557px] object-contain"
            initial={{ y: 0 }}
            animate={reduceMotion ? undefined : { y: [0, -20, 0] }}
            transition={{ duration: 3.6, times: [0, 0.5, 1], ease: 'easeInOut', repeat: Infinity }}
          />
          <img src={laptop} alt="BilionPass Travels" className="absolute left-[368px] top-0 h-[402px] w-[665px] object-contain" />
          <motion.img
            src={phoneRight}
            alt=""
            className="absolute right-0 top-[47px] h-[394px] w-[556px] object-contain"
            initial={{ y: -18.488 }}
            animate={reduceMotion ? undefined : { y: [-18.488, -0.488, -18.488] }}
            transition={{ duration: 3.6, times: [0, 0.5, 1], ease: 'easeInOut', repeat: Infinity }}
          />
        </div>

        <div className="absolute left-[260px] top-[898px] z-20 h-[512px] w-[1400px] overflow-hidden rounded-[16px]">
          <motion.div
            className="flex h-full gap-[32px]"
            animate={{
              x:
                reduceMotion || servicePageTransition === 0
                  ? servicePageTarget
                  : [servicePageStart, servicePageAnticipation, servicePageTarget],
            }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    duration: 0.58,
                    times: [0, 0.18, 1],
                    ease: [[0.33, 1, 0.68, 1], [0.65, 0, 0.35, 1]],
                  }
            }
          >
            {servicePages.map((page, pageIndex) => (
              <div key={pageIndex} className="grid h-[512px] w-[1400px] shrink-0 grid-cols-2 grid-rows-2 gap-[32px]">
                {page.map((card) => (
                  <article
                    key={card.key}
                    className="relative flex h-[240px] gap-[32px] overflow-hidden rounded-[24px] bg-white p-[20px] text-[#355f72]"
                  >
                    <img src={card.image} alt="" className="h-[200px] w-[248px] shrink-0 rounded-[16px] object-cover" />
                    <div className="relative z-10 flex flex-col justify-between flex-1 min-w-0">
                      <div className="relative flex flex-col">
                        <h3 className="whitespace-nowrap text-[30px] font-medium leading-[1.5] text-[#114861]">
                          {t(`home.travels.cards.${card.key}.title`)}
                        </h3>
                        <img src={serviceQuote} alt="" className="h-[12px] w-[16px]" />
                        <p className="line-clamp-3 pr-[8px] text-[20px] leading-[1.5] opacity-80">
                          {t(`home.travels.cards.${card.key}.description`)}
                        </p>
                      </div>
                      <a
                        href={bilionpassServiceUrl(card.key, language)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-w-[116px] w-fit items-center justify-center gap-[4px] rounded-[40px] border-[1.2px] border-[#00a0e9] px-[16px] py-[8px] text-[16px] font-medium leading-[1.5] text-[#00a0e9] transition-colors hover:bg-[#00a0e9] hover:text-white"
                      >
                        {t('home.business.learnMore')}
                        <RightOutlined className="text-[12px]" />
                      </a>
                    </div>
                    <img src={card.decoration} alt="" className="pointer-events-none absolute right-[50px] top-[30px] size-[100px]" />
                  </article>
                ))}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="absolute left-1/2 top-[1458px] z-20 flex h-[48px] -translate-x-1/2 items-center justify-center gap-[36px] whitespace-nowrap font-normal leading-normal">
          <button
            type="button"
            onClick={() => changeServicePage(0)}
            disabled={activeServicePage === 0}
            className="relative size-[48px] shrink-0 border-0 bg-transparent p-0 opacity-100 transition-opacity disabled:cursor-default disabled:opacity-30"
            aria-label={t('home.business.previousPage')}
          >
            <img src={pageArrowRight} alt="" className="absolute inset-0 size-full" />
          </button>
          <div className="flex shrink-0 items-center gap-[12px]">
            <span className="text-[30px] text-white">{activeServicePage + 1}</span>
            <span className="text-[21px] text-white/50">/</span>
            <span className="text-[30px] text-white opacity-30">2</span>
          </div>
          <button
            type="button"
            onClick={() => changeServicePage(1)}
            disabled={activeServicePage === 1}
            className="relative size-[48px] shrink-0 rotate-180 border-0 bg-transparent p-0 opacity-100 transition-opacity disabled:cursor-default disabled:opacity-30"
            aria-label={t('home.business.nextPage')}
          >
            <img src={pageArrowRight} alt="" className="absolute inset-0 size-full" />
          </button>
        </div>
      </Canvas>
    </section>
  );
};

const ShopsSection = () => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const descriptionLeading = i18n.resolvedLanguage === 'en-US' ? 'leading-[1.6]' : 'leading-[1.8]';

  const featuresTitle = i18n.resolvedLanguage === 'en-US' ? 'text-[26px]' : 'text-[32px]';
  const featuresDesc = i18n.resolvedLanguage === 'en-US' ? 'text-[20px]' : 'text-[24px]';

  return (
    <section data-journey-stop="shops" className="relative -mt-[86px] h-[1428px] overflow-hidden rounded-t-[80px] bg-[#dff1ff] z-[4]">
      <Canvas>
        <h2 className="absolute left-1/2 top-[102px] z-20 -translate-x-1/2 whitespace-nowrap font-bold leading-[1.5] text-[#2f5e77]">
          <span className="font-expose text-[80px]">BilionPass Shops</span>
          <span className="font-expose text-[65px]"> {t('home.shops.platform')}</span>
        </h2>

        <div
          className="absolute left-[1238px] top-[73px] z-30 h-[42px] w-[101px]"
          aria-label="To Shops"
        >
          <img
            src={toShopsBubble}
            alt=""
            className="absolute -left-[4px] top-[6px] h-[45px] w-[120px]"
          />
          <span className="absolute left-[5px] top-[10px] whitespace-nowrap text-[18px] font-medium leading-[1.5] text-white">
            To Shops
          </span>
        </div>

        <div className={`absolute left-[260px] top-[264px] z-20 w-[699px] text-[28px] ${descriptionLeading} text-[#355f72]`}>
          <p>{t('home.shops.descriptionLine1')}</p>
          <p className="mt-[8px]">{t('home.shops.descriptionLine2')}</p>
        </div>
        <img src={shopDevices} alt="BilionPass Shops" className="absolute left-[1005px] top-[245px] z-20 h-[417px] w-[685px] object-contain" />

        <div className="absolute left-[260px] top-[694px] z-20 flex h-[370px] w-[1400px] items-center justify-around rounded-[70px] bg-white px-[60px]">
          {shopFeatures.map((feature, index) => (
            <motion.div
              key={feature.key}
              className="flex w-[280px] flex-col items-center text-center"
              initial={reduceMotion ? false : { opacity: 0, y: 70 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.45 }}
              transition={{
                duration: reduceMotion ? 0 : 0.7,
                delay: reduceMotion ? 0 : index * 0.12,
                ease: [0.25, 1, 0.5, 1],
              }}
            >
              <img src={feature.icon} alt="" className="size-[120px]" />
              <h3 className={`whitespace-nowrap mt-[22px] white-no font-medium text-[#114861] ${featuresTitle}`}>{t(`home.shops.features.${feature.key}.title`)}</h3>
              <p className={`mt-[10px] leading-[1.4] text-[#57656c] opacity-80 ${featuresDesc}`}>{t(`home.shops.features.${feature.key}.description`)}</p>
            </motion.div>
          ))}
        </div>

        <img src={shopDecorLeft} alt="" className="absolute bottom-[60px] left-0 z-10 h-[296px] w-[353px] object-contain opacity-70" />
        <img src={shopDecorRight} alt="" className="absolute bottom-[120px] right-0 z-10 h-[431px] w-[451px] object-contain opacity-80" />
        <div className="absolute left-1/2 top-[1160px] z-20 -translate-x-1/2"><ViewMoreButton to="/shop" /></div>
      </Canvas>
    </section>
  );
};

const AdditionalSection = () => {
  const { t } = useTranslation();

  const contentLocation = i18n.resolvedLanguage === 'en-US' ? 'top-[290px]' : 'top-[320px]';
  const cardTitle = i18n.resolvedLanguage === 'en-US' ? 'text-[42px]' : 'text-[48px]';


  return (
    <section data-journey-stop="additional" className="relative -mt-[120px] h-[1051px] overflow-hidden rounded-t-[80px] bg-white z-[5]">
      <Canvas>
        <img src={additionalGlow} alt="" className="absolute -left-[250px] -top-[50px] size-[503px] opacity-70" />
        <h2 className="absolute left-1/2 top-[112px] z-20 -translate-x-1/2 font-douyin text-[70px] font-bold text-[#355f72]">{t('home.additional.title')}</h2>
        <div className="absolute left-[260px] top-[338px] z-20 flex gap-[40px]">
          {additionalCards.map((card) => (
            <div
              key={card.key}
              className="group relative h-[600px] w-[680px]"
              aria-label={card.href ? undefined : t(`home.additional.${card.key}.title`)}
              role={card.href ? undefined : 'img'}
            >
              {card.href && (
                <a
                  href={card.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t(`home.additional.${card.key}.title`)}
                  className="absolute inset-0 z-30 rounded-[24px] focus-visible:outline focus-visible:outline-4 focus-visible:outline-[#00a0e9]"
                />
              )}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  WebkitMaskImage: `url(${card.image})`,
                  WebkitMaskPosition: 'center',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: '100% 100%',
                  maskImage: `url(${card.image})`,
                  maskPosition: 'center',
                  maskRepeat: 'no-repeat',
                  maskSize: '100% 100%',
                }}
              >
                <img
                  src={card.image}
                  alt=""
                  className="absolute inset-0 size-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.08] motion-reduce:transform-none motion-reduce:transition-none"
                />
                <span
                  className="pointer-events-none absolute inset-0 opacity-0 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.08] group-hover:opacity-[0.78] motion-reduce:transform-none motion-reduce:transition-none"
                  style={{
                    backgroundColor: card.color,
                  }}
                />
              </div>
              <div className={`absolute left-[40px] right-[40px] top-[320px] text-white ${contentLocation}`}>
                <h3 className={`font-semibold leading-[1.5] whitespace-nowrap ${cardTitle}`}>{t(`home.additional.${card.key}.title`)}</h3>
                <p className="mt-[12px] text-[28px] leading-[1.6] whitespace-pre-line line-clamp-2">{t(`home.additional.${card.key}.description`)}</p>
              </div>
              <div className="absolute left-[40px] top-[518px] flex gap-[8px]">
                {[1, 2, 3].map((tag) => (
                  <span key={tag} className="rounded-[4px] bg-white/90 px-[16px] py-[5px] text-[16px] leading-[1.5]" style={{ color: card.color }}>
                    {t(`home.additional.${card.key}.tag${tag}`)}
                  </span>
                ))}
              </div>
              <span
                className={`absolute bottom-0 flex size-[84px] items-center justify-center rounded-full ${
                  card.key === 'marketing' ? 'right-[3px]' : 'right-0'
                }`}
              >
                {card.key === 'marketing' && (
                  <span className="absolute inset-[1px] rounded-full border-2 border-white bg-[#00a0e9]" />
                )}
                <img
                  src={card.arrow}
                  alt=""
                  className={`${
                    card.key === 'marketing' ? 'relative h-[34px] w-[44px]' : 'absolute inset-0 size-full'
                  } transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:rotate-[-45deg] motion-reduce:transition-none`}
                />
              </span>
            </div>
          ))}
        </div>
      </Canvas>
    </section>
  );
};

const NewsSection = () => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const designScale = useDesignScale();
  const [newsRecords, setNewsRecords] = useState<NewsRecord[]>([]);
  const [activeNewsPage, setActiveNewsPage] = useState(0);

  const newsPages = Array.from(
    { length: Math.min(NEWS_MAX_PAGES, Math.ceil(newsRecords.length / NEWS_PAGE_SIZE)) },
    (_, pageIndex) => newsRecords.slice(pageIndex * NEWS_PAGE_SIZE, (pageIndex + 1) * NEWS_PAGE_SIZE),
  );
  const newsPageCount = Math.max(1, newsPages.length);
  const newsPageDistance = 1425 * designScale;

  useEffect(() => {
    let ignore = false;

    setActiveNewsPage(0);

    newsPage({ current: 1, size: 9, status: 1 })
      .then(result => {
        if (ignore || result.code !== 0) return;
        setNewsRecords(result.data?.records?.slice(0, NEWS_PAGE_SIZE * NEWS_MAX_PAGES) ?? []);
      })
      .catch(() => {
        if (!ignore) setNewsRecords([]);
      });

    return () => {
      ignore = true;
    };
  }, [i18n.resolvedLanguage]);

  const changeNewsPage = (nextPage: number) => {
    if (nextPage < 0 || nextPage >= newsPageCount || nextPage === activeNewsPage) return;
    setActiveNewsPage(nextPage);
  };

  return (
    <section data-journey-stop="news" className="relative h-[1154px] overflow-hidden bg-[linear-gradient(180deg,#fff_0%,#e4f2fb_63%,#fff_100%)] z-[6]">
      <Canvas>
        <div className="home-business-news-offset">
          <h2 className="absolute left-1/2 top-[74px] z-20 -translate-x-1/2 font-douyin text-[70px] font-bold text-[#355f72]">{t('home.news.title')}</h2>
          <div className="absolute left-[260px] top-[293px] z-20 h-[585px] w-[1400px] overflow-hidden">
            <motion.div
              className="flex h-full gap-[25px]"
              animate={{ x: -activeNewsPage * newsPageDistance }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
            >
              {newsPages.map((page, pageIndex) => (
                <div key={pageIndex} className="flex h-[585px] w-[1400px] shrink-0 gap-[25px]">
                  {page.map((record, index) => {
                    const date = record.publishTime || record.createTime || '';
                    const formattedDate = date ? date.slice(0, 10).replace(/-/g, '/') : '--/--/--';

                    return (
                      <article key={record.id ?? `${date}-${pageIndex}-${index}`} className="h-[585px] w-[450px] shrink-0 overflow-hidden rounded-[24px] bg-[linear-gradient(197deg,rgba(255,255,255,0)_55%,#fff_80%)]">
                        <Link
                          to={`/news-detail?id=${record.id ?? ''}`}
                          className="group block h-full w-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#00a0e9]/30"
                          aria-label={record.title || record.summary || t('home.news.title')}
                        >
                          <img src={record.coverImg || fallbackNewsImages[index % fallbackNewsImages.length]} alt="" className="h-[300px] w-[450px] rounded-[24px] object-cover transition-transform duration-500 group-hover:scale-105" />
                          <div className="px-[20px] pt-[40px]">
                            <time dateTime={date} className="text-[22px] leading-[1.6] text-[#666]">{formattedDate}</time>
                            <h3 className="mt-[5px] line-clamp-4 text-[28px] leading-[1.6] text-[#355f72]">{record.title || record.summary || ''}</h3>
                          </div>
                        </Link>
                      </article>
                    );
                  })}
                </div>
              ))}
            </motion.div>
          </div>
          <div className="absolute left-1/2 top-[968px] z-20 flex -translate-x-1/2 gap-[10px]" role="tablist" aria-label={t('home.news.title')}>
            {Array.from({ length: newsPageCount }, (_, pageIndex) => (
              <button
                key={pageIndex}
                type="button"
                role="tab"
                aria-selected={activeNewsPage === pageIndex}
                aria-label={`${t('home.news.title')} ${pageIndex + 1}`}
                onClick={() => changeNewsPage(pageIndex)}
                className={`size-[12px] cursor-pointer rounded-full border-0 p-0 transition-colors motion-reduce:transition-none ${
                  activeNewsPage === pageIndex ? 'bg-[#ff6b2c]' : 'bg-[#d5d5d5]'
                }`}
              />
            ))}
          </div>
        </div>
      </Canvas>
    </section>
  );
};

export const ContactSection = ({ overlapPrevious = true }: { overlapPrevious?: boolean }) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage === 'en-US';
  const [taglineBeforeBrand = '', taglineAfterBrand = ''] = t('home.contact.tagline').split('BilionPass');

  return (
    <section
      id="home-contact"
      data-journey-stop="contact"
      className={`home-business-contact-section relative h-[873px] w-screen max-w-[100vw] overflow-hidden bg-white z-[7] ${overlapPrevious ? 'home-business-contact-overlap' : ''}`}
    >
      <img src={contactMask} alt="" className="absolute inset-0 z-0 object-cover object-center w-full h-full" />
      <Canvas>
        <div className="absolute left-[273px] top-[185px] z-20 text-white">
          <h2 className="font-expose text-[86px] font-bold leading-[0.995]">
            <span className="block">Expand Your</span><span className="block">Business With Us</span>
          </h2>
          <p className="max-w-[800px] whitespace-pre-line mt-[34px] flex items-center text-[46px] font-bold leading-[1.4]">
            {isEnglish ? (
              <span>{t('home.contact.tagline')}</span>
            ) : (
              <>
                <span className="block">{taglineBeforeBrand.trimEnd()}</span>
                <img
                  src={whiteLogo}
                  alt="BilionPass"
                  className="mx-[12px] h-[55px] w-[244px] shrink-0 object-contain translate-y-[-6px] block"
                />
                <span>{taglineAfterBrand.trimStart()}</span>
              </>
            )}
          </p>
          <p className="mt-[2px] text-[29px] leading-[1.5]">{t('home.contact.description')}</p>
          <a
            href="/contact"
            className="group mt-[58px] inline-flex h-[80px] w-[320px] items-center justify-center overflow-hidden rounded-[95px] border-2 border-[#ff5d21] bg-[#f9780b] text-[32px] text-white transition-[background-color,color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-white hover:text-[#f9780b] motion-reduce:duration-0"
          >
            <span className="flex items-center gap-[16px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-x-[27px] motion-reduce:duration-0">
              <img
                src={messageIcon}
                alt=""
                className="size-[38px] opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
              />
              {t('common.navigation.contactUs')}
            </span>
          </a>
        </div>
      </Canvas>
    </section>
  );
};

const HomeBusiness = () => (
  <div className="relative -mt-[103px] overflow-hidden">
    <TravelsSection />
    <ShopsSection />
    <AdditionalSection />
    <NewsSection />
    <ContactSection />
    <JourneyPlane />
  </div>
);

export default HomeBusiness;
